import { Box } from '@mui/material';
import { GuestJourneyRetentionCards } from 'api/reports-service/guest-journey-reports.types';
import KpiCard from '../kpi-card/kpi-card.component';
import LimitedDataCard from '../kpi-card/limited-data-card.component';

export type RetentionLtvCardsProps = {
  cards?: GuestJourneyRetentionCards;
  /**
   * Length of the selected sign-up date range, in days. Drives the
   * "Limited data" state — the BE doesn't flag it (see the API reconciliation
   * doc §5), so the FE compares the range against each metric's horizon.
   */
  signUpRangeDays?: number;
  loading?: boolean;
};

type LtvCardConfig = {
  /** Field name in the keyed BE cards response. */
  id: keyof GuestJourneyRetentionCards;
  /** Minimum range (days) required to compute this metric. */
  minDays: number;
};

// Card order is an FE decision; titles and values arrive on the cards.
const LTV_CONFIG: LtvCardConfig[] = [
  { id: 'totalSpendFirst30Days', minDays: 30 },
  { id: 'totalSpendFirst90Days', minDays: 90 },
  { id: 'totalSpendFirst180Days', minDays: 180 },
  { id: 'totalSpendFirst365Days', minDays: 365 },
];

const RetentionLtvCards = ({
  cards,
  signUpRangeDays,
  loading = false,
}: RetentionLtvCardsProps) => {
  return (
    <Box
      display="grid"
      gap={2}
      gridTemplateColumns={{
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(4, 1fr)',
      }}
      data-testid="Retention.LtvCards"
    >
      {LTV_CONFIG.map(({ id, minDays }) => {
        const card = cards?.[id];
        const testId = `Retention.LtvCard.${id}`;
        const limited = signUpRangeDays != null && signUpRangeDays < minDays;

        if (limited) {
          return (
            <LimitedDataCard
              key={id}
              title={card?.title}
              data-testid={testId}
            />
          );
        }

        return (
          <KpiCard
            key={id}
            card={card}
            loading={loading}
            data-testid={testId}
          />
        );
      })}
    </Box>
  );
};

export default RetentionLtvCards;
